import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { AccountActions, InvestmentActions } from '@portal/actions';
import PanelSidebar from '@portal/components/PanelSidebar/PanelSidebar';
import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import ApprovedNavigationStack from '@portal/pages/Approved/ApprovedNavigationStack';
import CardsNavigationStack from '@portal/pages/Cards/CardsNavigationStack';
import CashbackNavigationStack from '@portal/pages/Cashback/CashbackNavigationStack';
import CondominiumsNavigationStack from '@portal/pages/Condominiums/CondominiumsNavigatorStack';
import EmployeesNavigationStack from '@portal/pages/Employees/EmployeesNavigationStack';
import EscrowNavigationStack from '@portal/pages/Escrow/EscrowPaginationStack';
import ExtractNavigationStack from '@portal/pages/Extract/ExtractNavigationStack';
import HomeNavigationStack from '@portal/pages/Home/HomeNavigationStack';
import InvestmentPositionNavigationStack from '@portal/pages/InvestmentPosition/InvestmentPositionNavigationStack';
import AvailableInvestmentProductsNavigationStack from '@portal/pages/InvestmentProductsAvailable/AvailableInvestmentProductsNavigationStack';
import InvestmentsNavigationStack from '@portal/pages/Investments/InvestmentsNavigationStack';
import LoanNavigationStack from '@portal/pages/Loan/LoanNavigationStack';
import MyAccountNavigationStack from '@portal/pages/MyAccount/MyAccountNavigationStack';
import NotFound from '@portal/pages/NotFound/NotFound';
import PaymentNavigationStack from '@portal/pages/Payment/PaymentNavigationStack';
import PayslipNavigationStack from '@portal/pages/Payslip/PayslipNavigationStack';
import PixNavigationStack from '@portal/pages/Pix/PixNavigationStack';
import ProfileManagerNavigationStack from '@portal/pages/ProfileManager/ProfileManagerNavigationStack';
import TradingNotesNavigationStack from '@portal/pages/TradingNotes/TradingNotesNavigationStack';
import TransferNavigationStack from '@portal/pages/Transfer/TransferNavigationStack';
import UserManagerNavigationStack from '@portal/pages/UserManager/UserManagerNavigationStack';
import { maskCpfOrCnpj } from '@portal/services/masks';
import { getItem } from '@portal/services/storage';
import { moneyFormat } from '@portal/utils/format';
import { hasAccess } from '@portal/utils/hasAccess';
import { getPaymentAccountByType } from '@portal/utils/paymentAccount';
import { Route, Switch, useLocation } from 'react-router-dom';
import {
  availableSoon,
  cards,
  cashback,
  condominiums,
  employees,
  escrow,
  home,
  investments,
  myAccount,
  payment,
  payslip,
  pix,
  tradingNotes,
  transfer,
} from '~/config/app.json';
import {
  getRoutes,
  getRouteStackPath,
  getStackPath,
  isRouteAllowed,
} from '~/config/routes';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Loading from '@portal/components/Loading/Loading';
import ProtectedRoute from '@portal/components/ProtectedRoute/ProtectedRoute';
import { useNavigation } from '@portal/hooks/useNavigation';
import AccountSplitNavigationStack from '@portal/pages/AccountSplit/AccountSplitNavigationStack';
import LimitsNavigationStack from '@portal/pages/Limits/LimitsNavigationStack';
import NewReceivableNavigationStack from '@portal/pages/NewReceivable/NewReceivableNavigationStack';
import NotificationsNavigationStack from '@portal/pages/Notifications/NotificationsNavigationStack';
import ProductsNavigationStack from '@portal/pages/Products/ProductsNavigationStack';
import ReceiptsNavigationStack from '@portal/pages/Receipts/ReceiptsNavigationStack';
import RechargeNavigationStack from '@portal/pages/Recharge/RechargeNavigationStack';

const PanelNavigationStack: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const location = useLocation();

  const {
    user: { roles, me: loggedUser, cardEligibility },
    account: { balance, investmentPositionCount },
    paymentAccount: { selectedPaymentAccount },
    investments: { investmentApplicationsStatementAccount },
  } = useReduxState();
  const isBalanceVisible: boolean = JSON.parse(
    getItem('isBalanceVisible') ?? 'false'
  );

  const formatName = () => {
    return loggedUser?.account?.name ?? '';
  };

  const paymentAccount = loggedUser.account?.paymentAccounts?.find(
    (paymentAccount) => paymentAccount.bankAccount
  );

  const investmentAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.INVESTMENTS
  );

  const escrowAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.ESCROW
  );

  const loanAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.LOAN
  );

  useEffect(() => {
    if (
      (location.pathname === '/' && selectedPaymentAccount) ||
      investmentAccount
    ) {
      navigate(getRouteStackPath('HOME', 'HOME_PAGE'));
      return;
    }

    if (!selectedPaymentAccount && escrowAccount) {
      navigate(getRouteStackPath('ESCROW', 'ESCROW_PAGE'));
      return;
    }

    if (!selectedPaymentAccount && loanAccount) {
      navigate(getRouteStackPath('LOAN', 'LOAN_PAGE'));
      return;
    }
  }, [
    location.pathname,
    selectedPaymentAccount,
    investmentAccount,
    escrowAccount,
    loanAccount,
    history,
  ]);

  useEffect(() => {
    if (
      !balance &&
      hasAccess(roles, RolesType.BALANCE_READ) &&
      (selectedPaymentAccount || investmentAccount) &&
      isBalanceVisible
    ) {
      if (selectedPaymentAccount) {
        dispatch(AccountActions.getUserBalance(selectedPaymentAccount.id));
      } else if (investmentAccount) {
        dispatch(AccountActions.getUserBalance(investmentAccount.id));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, dispatch]);

  useEffect(() => {
    if (investments.enabled) {
      if (investments.simpleInvestment) {
        dispatch(InvestmentActions.getInvestmentStatement());
      } else {
        dispatch(AccountActions.getInvestmentPosition());
      }
    }
  }, [dispatch]);

  const showInvestmentsCondition = investments.onlyForExistingInvestments
    ? (investments.enabled && investmentPositionCount > 0) ||
      investmentApplicationsStatementAccount > 0
    : investments.enabled;

  const routes = getRoutes(loggedUser.account.type, loggedUser, {
    showInvestments: showInvestmentsCondition,
    cardEligibility,
  });

  if (!loggedUser) {
    return <Loading />;
  }

  const canAccess = isRouteAllowed(
    routes,
    location.pathname,
    loggedUser.account.type ?? AccountTaxIdentifierType.PERSON,
    roles
  );

  if (!canAccess) {
    return <NotFound />;
  }

  const filterRoutes = (routes: models.Route[]) => {
    return routes.filter((route) => route.id !== 'LIMITS');
  };

  return (
    <div className="panel-navigation-stack">
      <div className="panel-navigation-stack__sidebar">
        <PanelSidebar
          routes={filterRoutes(routes)}
          name={formatName()}
          taxIdentifier={maskCpfOrCnpj(loggedUser.account.taxIdentifier)}
          balance={
            balance !== null ? moneyFormat(Number(balance.available)) : null
          }
          rawBalance={balance !== null ? Number(balance.available) : null}
          overdraft={
            balance !== null ? moneyFormat(Number(balance.overdraft)) : null
          }
          rawOverdraft={balance !== null ? Number(balance.overdraft) : null}
          account={paymentAccount?.bankAccount ?? ''}
          bankCode={paymentAccount?.bankNumber ?? myAccount.bankCode}
          agency={paymentAccount?.bankBranchAccount ?? ''}
          bankAccount={investmentAccount?.bankAccount ?? ''}
          investmentProfile={investmentAccount?.investmentProfile}
        />
      </div>

      <div className="panel-navigation-stack__content">
        <Switch>
          {selectedPaymentAccount || investmentAccount || escrowAccount ? (
            <Route path={getStackPath('HOME')}>
              <HomeNavigationStack />
            </Route>
          ) : null}

          <Route path="/notificacoes">
            <NotificationsNavigationStack />
          </Route>

          {(pix.enabled || availableSoon.pix) && selectedPaymentAccount ? (
            <Route path={getStackPath('PIX')}>
              <PixNavigationStack />
            </Route>
          ) : null}

          {selectedPaymentAccount || investmentAccount ? (
            <Route path={getStackPath('TRANSFERS')}>
              <TransferNavigationStack />
            </Route>
          ) : null}

          {selectedPaymentAccount || investmentAccount ? (
            <ProtectedRoute
              exact
              path={getStackPath('EXTRACT')}
              roles={RolesType.TRANSACTION_READ}
            >
              <ExtractNavigationStack />
            </ProtectedRoute>
          ) : null}

          <Route path={getStackPath('LOAN')}>
            <LoanNavigationStack />
          </Route>

          {transfer.twoFactorApprovals ? (
            <ProtectedRoute
              path={getStackPath('APPROVED')}
              roles={[
                RolesType.TRANSACTION_APPROVER,
                RolesType.TRANSACTION_READ,
              ]}
            >
              <ApprovedNavigationStack />
            </ProtectedRoute>
          ) : null}

          {(cashback.enabled || cashback.money) && selectedPaymentAccount ? (
            <Route path={getStackPath('CASHBACK')}>
              <CashbackNavigationStack />
            </Route>
          ) : null}

          {cards.enabled && selectedPaymentAccount ? (
            <ProtectedRoute
              path={getStackPath('CARDS')}
              roles={[RolesType.CARD_MANAGE, RolesType.CARD_READ]}
            >
              <CardsNavigationStack />
            </ProtectedRoute>
          ) : null}

          {escrow.enabled &&
          loggedUser.account.type === AccountTaxIdentifierType.CORPORATE &&
          escrowAccount ? (
            <Route path={getStackPath('ESCROW')}>
              <EscrowNavigationStack />
            </Route>
          ) : null}

          {payment.accountReceivable && selectedPaymentAccount ? (
            <Route path={getStackPath('RECEIVABLE')}>
              <PaymentNavigationStack />
            </Route>
          ) : null}

          {payment.newAccountReceivable && selectedPaymentAccount ? (
            <Route path={getStackPath('NEW_RECEIVABLE')}>
              <NewReceivableNavigationStack />
            </Route>
          ) : null}

          {payment.accountPayable && selectedPaymentAccount ? (
            <Route path={getStackPath('PAYABLE')}>
              <PaymentNavigationStack />
            </Route>
          ) : null}

          {(payment.accountPayable || payment.accountReceivable) &&
          selectedPaymentAccount ? (
            <Route path={getStackPath('FILES_HANDLER')}>
              <PaymentNavigationStack />
            </Route>
          ) : null}

          {transfer.batchReceipts ? (
            <ProtectedRoute
              path={getStackPath('RECEIPTS')}
              roles={RolesType.TRANSACTION_READ}
            >
              <ReceiptsNavigationStack />
            </ProtectedRoute>
          ) : null}

          <Route path={getStackPath('PROFILE')}>
            <MyAccountNavigationStack />
          </Route>

          {showInvestmentsCondition ? (
            <Route path={getStackPath('INVESTMENTS')}>
              <InvestmentsNavigationStack />
            </Route>
          ) : null}

          {showInvestmentsCondition ? (
            <Route path={getStackPath('INVESTMENT_PRODUCTS_AVAILABLE')}>
              <AvailableInvestmentProductsNavigationStack />
            </Route>
          ) : null}

          {showInvestmentsCondition && investments.investmentPosition ? (
            <Route path={getStackPath('INVESTMENT_POSITION')}>
              <InvestmentPositionNavigationStack />
            </Route>
          ) : null}

          {showInvestmentsCondition && tradingNotes.enabled ? (
            <Route path={getStackPath('TRADING_NOTES')}>
              <TradingNotesNavigationStack />
            </Route>
          ) : null}

          <Route path={getStackPath('USER_MANAGER')}>
            <UserManagerNavigationStack />
          </Route>

          <Route path={getStackPath('PROFILE_MANAGER')}>
            <ProfileManagerNavigationStack />
          </Route>

          {employees.enabled ? (
            <Route path={getStackPath('EMPLOYEES')}>
              <EmployeesNavigationStack />
            </Route>
          ) : null}

          {employees.enabled ? (
            <Route path={getStackPath('APPROVE_PAYMENTS')}>
              <EmployeesNavigationStack />
            </Route>
          ) : null}

          {condominiums.enabled ? (
            <Route path={getStackPath('CONDOMINIUMS')}>
              <CondominiumsNavigationStack />
            </Route>
          ) : null}

          {payslip.enabled ? (
            <Route path={getStackPath('PAYSLIP')}>
              <PayslipNavigationStack />
            </Route>
          ) : null}

          {myAccount.accountLimits ? (
            <Route path={getStackPath('LIMITS')}>
              <LimitsNavigationStack />
            </Route>
          ) : null}
          <Route path={getStackPath('ACCOUNT_SPLIT')}>
            <AccountSplitNavigationStack />
          </Route>
          {home.enableRecharge ? (
            <Route path={getStackPath('RECHARGE')}>
              <RechargeNavigationStack />
            </Route>
          ) : null}
          <Route path={getStackPath('PRODUCTS')}>
            <ProductsNavigationStack />
          </Route>
          {/* TODO: Liberar quando JD disponibilizar  */}
          {/* <Route path={getStackPath('OVERDRAFT')}>
            <PanelContent pageTitle={translate('PAGES.OVERDRAFT.TITLE')}>
              <OverdraftPage />
            </PanelContent>
          </Route> */}
        </Switch>
      </div>
    </div>
  );
};

export default PanelNavigationStack;
